import { Controller } from '@hotwired/stimulus'

// Replaces the native <select> popup with a styled listbox. Keeps the real
// <select> in the DOM (sr-only) so Rails forms and existing change listeners work.
export default class extends Controller {
  static targets = ['native', 'trigger', 'label', 'panel', 'option', 'search', 'empty']

  static values = {
    open: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    placeholder: { type: String, default: 'Select…' }
  }

  connect () {
    this.onDocPointer = this.onDocPointer.bind(this)
    this.onNativeChange = this.onNativeChange.bind(this)
    this.buildUi()
    this.syncFromNative()
    this.nativeTarget.addEventListener('change', this.onNativeChange)
    document.addEventListener('pointerdown', this.onDocPointer)
  }

  disconnect () {
    this.nativeTarget?.removeEventListener('change', this.onNativeChange)
    document.removeEventListener('pointerdown', this.onDocPointer)
    this.ui?.remove()
  }

  buildUi () {
    if (this.ui) this.ui.remove()

    this.nativeTarget.classList.add('pretty-select__native')
    this.nativeTarget.setAttribute('tabindex', '-1')
    this.nativeTarget.setAttribute('aria-hidden', 'true')

    this.ui = document.createElement('div')
    this.ui.className = 'pretty-select__ui'
    this.ui.innerHTML = `
      <button type="button"
              class="pretty-select__trigger"
              data-pretty-select-target="trigger"
              data-action="click->pretty-select#toggle keydown->pretty-select#onTriggerKeydown"
              aria-haspopup="listbox"
              aria-expanded="false">
        <span class="pretty-select__label" data-pretty-select-target="label"></span>
        <span class="pretty-select__chevron" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="currentColor" class="h-4 w-4"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd"/></svg>
        </span>
      </button>
      <div class="pretty-select__panel hidden"
           data-pretty-select-target="panel"
           role="listbox"
           hidden>
        ${this.searchableValue ? `
          <div class="pretty-select__search-wrap">
            <input type="search"
                   class="pretty-select__search"
                   placeholder="Search…"
                   autocomplete="off"
                   data-pretty-select-target="search"
                   data-action="input->pretty-select#filter keydown->pretty-select#onSearchKeydown">
          </div>
        ` : ''}
        <div class="pretty-select__options" data-pretty-select-options></div>
        <p class="pretty-select__empty hidden" data-pretty-select-target="empty">No matches</p>
      </div>
    `

    this.element.appendChild(this.ui)
    this.renderOptions()
  }

  renderOptions () {
    const box = this.ui.querySelector('[data-pretty-select-options]')
    if (!box) return

    box.innerHTML = ''
    Array.from(this.nativeTarget.options).forEach((opt, index) => {
      if (opt.disabled && opt.value === '' && opt.text.trim() === '') return

      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'pretty-select__option'
      btn.dataset.prettySelectTarget = 'option'
      btn.dataset.value = opt.value
      btn.dataset.label = opt.text
      btn.dataset.index = String(index)
      btn.setAttribute('role', 'option')
      btn.setAttribute('data-action', 'click->pretty-select#choose')
      if (opt.disabled) {
        btn.disabled = true
        btn.classList.add('is-disabled')
      }
      btn.innerHTML = `
        <span class="pretty-select__option-text">${this.escape(opt.text)}</span>
        <span class="pretty-select__check" aria-hidden="true">
          <svg viewBox="0 0 20 20" fill="currentColor" class="h-4 w-4"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"/></svg>
        </span>
      `
      box.appendChild(btn)
    })
  }

  syncFromNative () {
    const selected = this.nativeTarget.selectedOptions[0]
    const text = selected?.text?.trim() || this.placeholderValue
    if (this.hasLabelTarget) this.labelTarget.textContent = text

    this.optionTargets.forEach((option) => {
      const isSelected = option.dataset.value === this.nativeTarget.value
      option.classList.toggle('is-selected', isSelected)
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false')
    })
  }

  onNativeChange () {
    this.syncFromNative()
  }

  toggle (event) {
    event?.preventDefault()
    if (this.openValue) this.close()
    else this.open()
  }

  open () {
    if (this.nativeTarget.disabled) return

    this.renderOptions()
    this.syncFromNative()
    this.openValue = true
    this.panelTarget.classList.remove('hidden')
    this.panelTarget.hidden = false
    this.triggerTarget.setAttribute('aria-expanded', 'true')
    this.triggerTarget.classList.add('is-open')
    this.element.classList.add('is-open')

    if (this.hasSearchTarget) {
      this.searchTarget.value = ''
      this.filter()
      requestAnimationFrame(() => this.searchTarget.focus())
    } else {
      const selected = this.optionTargets.find((o) => o.classList.contains('is-selected'))
      selected?.focus()
    }
  }

  close () {
    this.openValue = false
    this.panelTarget.classList.add('hidden')
    this.panelTarget.hidden = true
    this.triggerTarget.setAttribute('aria-expanded', 'false')
    this.triggerTarget.classList.remove('is-open')
    this.element.classList.remove('is-open')
  }

  choose (event) {
    event.preventDefault()
    const button = event.currentTarget
    if (button.disabled) return

    const value = button.dataset.value ?? ''
    this.nativeTarget.value = value
    this.nativeTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.syncFromNative()
    this.close()
    this.triggerTarget.focus()
  }

  filter () {
    if (!this.hasSearchTarget) return

    const query = this.searchTarget.value.trim().toLowerCase()
    let visible = 0

    this.optionTargets.forEach((option) => {
      const label = (option.dataset.label || '').toLowerCase()
      const match = !query || label.includes(query)
      option.classList.toggle('hidden', !match)
      if (match) visible += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle('hidden', visible > 0)
    }
  }

  onDocPointer (event) {
    if (!this.openValue) return
    if (!this.element.contains(event.target)) this.close()
  }

  onTriggerKeydown (event) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      this.open()
    } else if (event.key === 'Escape') {
      this.close()
    }
  }

  onSearchKeydown (event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
      this.triggerTarget.focus()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const first = this.optionTargets.find((o) => !o.classList.contains('hidden') && !o.disabled)
      if (first) first.click()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      const first = this.optionTargets.find((o) => !o.classList.contains('hidden') && !o.disabled)
      first?.focus()
    }
  }

  escape (text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }
}
